import type { FileCabinetChannel, FileCabinetDirection, FileCabinetViewKey } from './file-cabinet.types';
import { FILE_CABINET_CHANNELS, FILE_CABINET_VIEWS } from './file-cabinet.views';

/**
 * Where the File Cabinet is, as URL parameters — so a record can be linked to, and a reload lands on
 * the same tab with the same drawer open.
 *
 * - `tab` — the channel; `dir` — its direction; `open` — the drawer, `<view>:<id>`.
 * - **Never the search text, nor any filter.** A search here is a patient's name or a member id in
 *   scanned mail; in a URL it lands in browser history, server logs, Sentry breadcrumbs and every
 *   link someone pastes into a chat. Record keys are opaque ids.
 * - Anything unrecognised is ignored, not guessed at: a link from an older build opens the default tab.
 */

export interface FileCabinetLocation {
    channel?: FileCabinetChannel;
    direction?: FileCabinetDirection;
    open?: { view: FileCabinetViewKey; id: string };
}

const TAB = 'tab';
const DIR = 'dir';
const OPEN = 'open';
/** A record key — uuid-like. Anything else in `open` is not ours, and is not read. */
const ID = /^[A-Za-z0-9-]{1,64}$/;

export function fileCabinetLocationFrom(params: URLSearchParams): FileCabinetLocation {
    const location: FileCabinetLocation = {};
    const channel = FILE_CABINET_CHANNELS.find(c => c.channel === params.get(TAB));
    if (channel) {
        location.channel = channel.channel;
        const direction = channel.directions.find(d => d.direction === params.get(DIR) && d.view);
        if (direction) {
            location.direction = direction.direction;
        }
    }
    const open = params.get(OPEN);
    if (open) {
        const at = open.lastIndexOf(':');
        const view = open.slice(0, at) as FileCabinetViewKey;
        const id = open.slice(at + 1);
        if (at > 0 && view in FILE_CABINET_VIEWS && ID.test(id)) {
            location.open = { view, id };
        }
    }
    return location;
}

/** `params` with the File Cabinet's own parameters set to `location`; every other parameter kept as is. */
export function fileCabinetLocationTo(params: URLSearchParams, location: FileCabinetLocation): URLSearchParams {
    const next = new URLSearchParams(params);
    const set = (key: string, value: string | undefined): void => (value ? next.set(key, value) : next.delete(key));
    set(TAB, location.channel && location.channel !== 'docs' ? location.channel : undefined);
    set(DIR, location.channel && location.channel !== 'docs' ? location.direction : undefined);
    set(OPEN, location.open && ID.test(location.open.id) ? `${location.open.view}:${location.open.id}` : undefined);
    return next;
}

/** The same place — so writing the store's location to the URL, and reading it back, is not a change. */
export function fileCabinetSameLocation(a: FileCabinetLocation, b: FileCabinetLocation): boolean {
    const channel = (l: FileCabinetLocation): FileCabinetChannel => l.channel ?? 'docs';
    return (
        channel(a) === channel(b) &&
        (channel(a) === 'docs' || a.direction === b.direction) &&
        a.open?.view === b.open?.view &&
        a.open?.id === b.open?.id
    );
}
